import type { GetUserDirectoryResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetUsersQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  createQueryHook,
  REFERENCE_DATA_STALE_TIME_MS,
  type StrictMappedGraphQLSelection,
} from 'src/utils';

type UseGetUsersArgs = Record<string, never>;

/**
 * Flat user list backing the legacy `getUsers` GraphQL query.
 *
 * Reads `frontend.user.directory`, whose repository orders by `FriendlyName`
 * ascending. The mapper repeats that primary sort while retaining its existing
 * null-as-empty behaviour. The server already filters out
 * `ThirdPartyRespondent` rows, so no client-side filtering is required.
 *
 * COI-753: this used to read `frontend.user.users`, the same wide procedure as
 * `useGetAuthUsers`, and project it down HERE — so the settings-admin payload
 * (`External_Id`, `DisplayName`, every colleague's group memberships, custom
 * roles and `organisationusers[].{RoleKey, External_Id, LastSeen}`) reached the
 * browser of every Standard user who opened a form with an owner picker before
 * being discarded. V2 kept the two reads apart under `read:user` and
 * `read:auth-user`; `directory` restores that. The mapping below is unchanged —
 * the procedure now returns exactly the fields it consumes.
 */
export const mapTrpcUsersToGraphQL = (
  trpcData: GetUserDirectoryResponseRow[]
): StrictMappedGraphQLSelection<GetUsersQuery> => ({
  user: [...trpcData]
    .sort((a, b) => (a.FriendlyName ?? '').localeCompare(b.FriendlyName ?? ''))
    .map((u) => ({
      Id: u.Id,
      FriendlyName: u.FriendlyName,
      // COI-714: the `getUsers` document this projects onto reads the
      // `risksmart.user_view_active` view, whose `Status` is org-sourced.
      // Take the org membership's `Status` so the projection keeps matching
      // it — the tenant-global `auth.user."Status"` it used to read is
      // last-writer-wins across orgs and is being decommissioned. The
      // nested array is the caller org's single membership row.
      Status: u.organisationusers?.[0]?.Status ?? null,
      // COI-927: `RoleKey` no longer falls back to the tenant-global column.
      // That fallback existed to mirror `user_view_active`'s
      // `COALESCE(o."RoleKey", u."RoleKey")` so the two legs of this hook agreed;
      // migration `1787681343000` removed the view's global arm and
      // `1787671223000` backfilled the membership column, so the membership role
      // IS the value on both legs now.
      //
      // `LastSeen` below keeps its fallback: that column was not backfilled and
      // its COALESCE is still in the view. `useGroupAndUserOptions` hides users
      // with no `LastSeen` from every owner/contributor picker, so dropping it
      // early would make users vanish from pickers on one leg only. It goes with
      // COI-830.
      RoleKey: u.organisationusers?.[0]?.RoleKey ?? null,
      Email: u.Email,
      Department: u.Department,
      JobTitle: u.JobTitle,
      OfficeLocation: u.OfficeLocation,
      LastSeen: u.organisationusers?.[0]?.LastSeen ?? u.LastSeen,
      IsCustomerSupport: u.IsCustomerSupport,
    })),
});

export const useGetUsers = createQueryHook<
  UseGetUsersArgs,
  GetUserDirectoryResponseRow[],
  GetUsersQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.user.directory.queryOptions(),
  mapTrpcDataToGraphQL: mapTrpcUsersToGraphQL,
  // Org reference data; writes invalidate it. See COI-882.
  trpcStaleTime: REFERENCE_DATA_STALE_TIME_MS,
  graphqlDocument: GetUsersDocument,
  // The user list is shared by ~8 field-config hooks that often render in the
  // same form. Use cache-first (Apollo's default, as the pre-migration
  // `useQuery(GetUsersDocument)` consumers had) so concurrent identical queries
  // dedupe into one request instead of the factory-default `no-cache`, which
  // fires one request per consumer.
  graphqlFetchPolicy: 'cache-first',
});
